/**
 * callableDiagnostics — tell "the request never left this browser" apart from
 * "the server ran it and failed". Walter 2026-08-07.
 *
 * WHY. The Firebase callable SDK reports BOTH of these as the same thing:
 * `FirebaseError: internal` (code `functions/internal`). A network-level
 * failure — an ad-blocker or privacy extension eating `cloudfunctions.net`, a
 * dead connection, a corporate proxy — is indistinguishable from a Cloud
 * Function that threw. That ambiguity is why Walter's "can't delete a list
 * from the tile ⋯ menu" report (#161) stalled: the toast we added named the
 * ad-blocker as a *guess*, and the guess couldn't be confirmed from the
 * message alone.
 *
 * So when a callable fails, we probe the functions origin directly. A
 * `no-cors` request resolves for ANY HTTP response (even 404/405 — the body is
 * opaque, which is fine; we only care that bytes came back). It REJECTS when
 * the request was blocked before it left the browser. That's the signal.
 */

import { functions } from './firebase';

const REGION = 'us-central1'; // getFunctions(app) default — matches deploys

function functionsOrigin() {
  const projectId = functions?.app?.options?.projectId;
  if (!projectId) return null;
  return `https://${REGION}-${projectId}.cloudfunctions.net`;
}

/**
 * canReachFunctions(name) → true | false | null
 *   true  — something answered: the server was reached, so the failure is
 *           server-side.
 *   false — the request was blocked/failed before reaching the network.
 *   null  — we couldn't tell (no project id); caller should stay vague.
 */
export async function canReachFunctions(name = 'trashList') {
  const origin = functionsOrigin();
  if (!origin) return null;
  try {
    await fetch(`${origin}/${name}`, { method: 'GET', mode: 'no-cors', cache: 'no-store' });
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * describeCallableFailure(err, { name }) → { reason, blocked }
 *
 * `reason` is a user-facing clause meant to be dropped into a toast after
 * "couldn't <verb> — ". `blocked` is true when the browser (not the server)
 * is what stopped the request, so callers can tailor the advice.
 */
export async function describeCallableFailure(err, { name = 'trashList' } = {}) {
  const code = (err?.code || '').replace('functions/', '');

  // Codes the server actually chose are already meaningful — pass them
  // through untouched (permission-denied, unauthenticated, invalid-argument,
  // not-found, resource-exhausted…). Only `internal` and `unavailable` are
  // ambiguous enough to be worth a probe.
  if (code && code !== 'internal' && code !== 'unavailable') {
    return { reason: err?.message || code, blocked: false };
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { reason: 'you\'re offline. This one needs a connection', blocked: false };
  }

  const reachable = await canReachFunctions(name);
  if (reachable === false) {
    return {
      reason: 'this browser blocked the request — usually an ad-blocker or privacy extension',
      blocked: true,
    };
  }
  if (reachable === true) {
    return {
      reason: `the server reached an error (${code || 'internal'}) and nothing was changed`,
      blocked: false,
    };
  }
  return { reason: err?.message || 'the server could not be reached', blocked: false };
}
